import React from "react";
import { makeDarkModeCss } from "../utils/darkModeUtils";

interface DocumentStylesProps {
  safeScopedCss: string;
  shouldGenerateDarkModeCss?: boolean;
}

const DOCUMENT_WATERMARK_STYLES = `
  .doc-watermark {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow-x: hidden;
    overflow-y: visible;
    z-index: 1;
    background-repeat: repeat-y;
    background-position: center top;
    /*
     * Width follows the element and height the tile's aspect ratio, so the
     * label fits even where the element is narrower than the tile was
     * measured (below 100% zoom), and the print tile spans the paper.
     */
    background-size: 100% auto;
  }

  @media print {
    /*
     * The law viewer's .print-reset makes every descendant position: static,
     * which collapsed the watermark to 0px, so law documents printed without
     * it. These selectors outrank its \`.print-reset *\`.
     */
    :has(> .doc-root + .doc-watermark) {
      position: relative !important;
    }

    .doc-root + .doc-watermark {
      position: absolute !important;
      /* Fixed proportions, half a sheet tall at most: a whole label on every page. */
      background-image: var(--watermark-print-image) !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .consolidation-changed {
    border-left: 3px solid var(--primary);
    padding-left: 0.75rem;
    margin-left: -0.75rem;
    background-color: color-mix(in oklch, var(--primary) 8%, transparent);
    border-radius: 0.25rem;
  }

  .consolidation-changed-clickable {
    cursor: pointer;
  }

  .consolidation-changed-clickable:hover {
    background-color: color-mix(in oklch, var(--primary) 12%, transparent);
  }

  /*
   * While the user is drag-selecting text (to create a reference), drop the
   * clickable affordance on changed sections so text selection wins over the
   * amendment click/hover. The class is toggled on .doc-root in DocumentRenderer.
   */
  .doc-root.consolidation-selecting .consolidation-changed-clickable {
    cursor: text;
  }

  .doc-root.consolidation-selecting .consolidation-changed-clickable:hover {
    background-color: transparent;
  }

  /*
   * Per-change approval annotation. The viewer always renders the full draft, so
   * a changed section whose amendment is not yet verified is flagged distinctly
   * with a warning accent and an "Udkast" tag. This is a persistent trust signal
   * (always on), independent of the "Fremhæv ændringer" highlight toggle, and
   * intentionally overrides the blue "changed" box when both apply so an
   * unverified change is never mistaken for a verified one. Approved changes get
   * no extra marker — the plain consolidated text is the trusted default.
   */
  .consolidation-change-draft {
    position: relative;
    overflow: visible;
    border-left: 3px solid var(--warning);
    padding-left: 0.75rem;
    margin-left: -0.75rem;
    background-color: color-mix(in oklch, var(--warning) 10%, transparent);
    border-radius: 0.25rem;
  }

  /* Badge sits outside the box, above the top-right corner. */
  .consolidation-change-draft::after {
    content: "Udkast \\00b7 ikke verificeret";
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    transform: translateY(calc(-100% - 0px));
    padding: 1px 6px;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--warning-foreground);
    background-color: var(--warning);
    border-radius: 0.25rem;
    pointer-events: none;
    white-space: nowrap;
  }

  /*
   * A grouped amendment point (e.g. an inserted group heading plus its
   * paragraphs) is wrapped so the whole run reads as one changed section. The
   * vertical padding establishes a margin-collapse boundary so the highlight
   * background fully contains its member containers.
   */
  .consolidation-change-group {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }

  /*
   * Keep the member containers visually distinct inside the shared box. A plain
   * margin collapses with the members' own paragraph margins and produces no
   * visible gap, so use non-collapsing padding plus a hairline divider to make
   * it obvious the box holds several separate elements.
   */
  .consolidation-change-group > [data-container-type][id]:not(:first-child) {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid color-mix(in oklch, var(--primary) 22%, transparent);
  }

  .consolidation-change-selected {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 0.25rem;
  }

  .consolidation-change-focused {
    box-shadow: 0 0 0 2px color-mix(in oklch, var(--primary) 45%, transparent);
    border-radius: 0.25rem;
  }

  /*
   * Deleted "ghost" containers: an amendment removed them, so they are absent
   * from the consolidated text and re-inserted at their original position only
   * to show what was removed. Hidden by default; revealed with a red highlight
   * and strikethrough when "Fremhæv ændringer" is on (.consolidation-highlights-on
   * on .doc-root). Colours mirror the compare-mode diff deletions.
   */
  .consolidation-deleted-ghost {
    display: none;
  }

  .doc-root.consolidation-highlights-on .consolidation-deleted-ghost {
    display: block;
    border-left: 3px solid hsl(0 84% 60% / 0.65);
    padding-left: 0.75rem;
    margin-left: -0.75rem;
    background-color: hsl(0 84% 60% / 0.08);
    border-radius: 0.25rem;
    text-decoration: line-through;
    text-decoration-color: hsl(0 70% 50% / 0.65);
    opacity: 0.85;
  }

  .doc-root.consolidation-highlights-on
    .consolidation-deleted-ghost.consolidation-changed-clickable {
    cursor: pointer;
  }

  /*
   * When highlighting is off, collapse group wrappers whose only members are
   * hidden ghosts so no empty padded box is left behind. Mixed groups (a live
   * change next to a deletion) keep rendering their surviving members.
   */
  .doc-root:not(.consolidation-highlights-on)
    .consolidation-change-group:has(> .consolidation-deleted-ghost):not(
      :has(> [data-container-type]:not(.consolidation-deleted-ghost))
    ) {
    display: none;
  }
`;

/**
 * Search and reference highlights, painted with the CSS Custom Highlight API
 * (ADR 0001). Rendered for every document, styled or not: a custom highlight
 * has no default look, so without these rules nothing would paint.
 *
 * A fill ladder, since ::highlight() takes no outline, padding or radius.
 * Priority from the top: search-current, search, reference-preview,
 * reference, so a search hit inside a referenced passage stays readable. The
 * current hit is told apart by a solid fill and is left out of search, so
 * fills never stack. Theme tokens resolve per .dark ancestor.
 */
const DOCUMENT_HIGHLIGHT_STYLES = `
  ::highlight(search) {
    background-color: var(--doc-highlight-search-bg);
    color: var(--foreground);
  }
  ::highlight(search-current) {
    background-color: var(--doc-highlight-border);
    color: var(--doc-highlight-search-current-fg);
  }
  /* OCR pages: the word the reader sees is in the page image, under a
     transparent SVG text layer that the highlight paints on, so a solid
     fill would cover it. Translucent there, still stronger than a hit. */
  .doc-root .t ::highlight(search-current) {
    background-color: color-mix(in oklch, var(--doc-highlight-border) 60%, transparent);
  }
  ::highlight(reference) {
    background-color: var(--doc-highlight-search-current-bg);
    color: var(--foreground);
  }
  /* The sidebar hover preview's blue has no theme token. */
  ::highlight(reference-preview) {
    background-color: oklch(0.72 0.14 250 / 0.42);
    color: var(--foreground);
  }
  .dark ::highlight(reference-preview) {
    background-color: oklch(0.55 0.12 250 / 0.55);
  }

  /* Printouts carry no highlights, as with the browser's own find-in-page.
     PrintStyles sets print-color-adjust: exact, which would print them.
     !important beats the .dark preview rule. */
  @media print {
    ::highlight(search),
    ::highlight(search-current),
    ::highlight(reference),
    ::highlight(reference-preview) {
      background-color: transparent !important;
      text-decoration: none !important;
      color: inherit !important;
    }
  }
`;

export const DocumentStyles: React.FC<DocumentStylesProps> = ({
  safeScopedCss,
  shouldGenerateDarkModeCss = true,
}) => {
  const darkModeCss = React.useMemo(
    () => makeDarkModeCss(safeScopedCss),
    [safeScopedCss]
  );

  if (!safeScopedCss) {
    return (
      <style
        dangerouslySetInnerHTML={{
          __html: DOCUMENT_WATERMARK_STYLES + DOCUMENT_HIGHLIGHT_STYLES,
        }}
      />
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: safeScopedCss }} />
      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Neutralize inherited center alignment from outer layout */
            .doc-root { 
              text-align: start; 
              position: relative;
              height: auto;
              overflow-y: visible;
              overflow-x: hidden;
            }
            
            /* Ensure content fits on print without clipping */
            @media print {
              .doc-root {
                overflow: visible !important;
                width: 100% !important;
                max-width: 100% !important;
              }
              /* Constrain common overflow elements during print */
              .doc-root img,
              .doc-root pre,
              .doc-root svg,
              .doc-root canvas {
                max-width: 100% !important;
              }
              .doc-root pre,
              .doc-root pre code {
                white-space: pre-wrap !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
              .doc-root table {
                width: 100% !important;
                max-width: 100% !important;
                table-layout: fixed !important;
              }
            }
            .doc-root p { text-align: start; }
            .doc-root .tl-content,
            .doc-root .paragraph-wrap { text-align: start; }

            /* Retsinformation silver HTML nests title text in <h2> inside classed
               wrappers (.Titel2/.AfsnitOverskrift/.KapitelOverskrift2). The class
               rules style the wrapper, but the unclassed <h2> falls through to the
               scoped Bootstrap "h2 { font-size: 2.1875rem }" reset and renders
               oversized. Neutralize h1-h6 sizing inside .document-content so the
               wrapper's own typography applies. The .dark twin out-specifies the
               dark-mode rule that makeDarkModeCss emits and injects last. */
            .doc-root .document-content :is(h1, h2, h3, h4, h5, h6),
            .dark .doc-root .document-content :is(h1, h2, h3, h4, h5, h6) {
              font-size: inherit;
              font-weight: inherit;
              font-style: inherit;
              line-height: inherit;
              margin: 0;
            }

            /* Override pdf2htmlEX #page-container to respect container height */
            .doc-root #page-container {
              position: relative !important;
              left: 0 !important;
              right: auto !important;
              bottom: auto !important;
              height: auto !important;
              max-height: none !important;
            }

            /* Existing highlight styling */
            .doc-root .doc-highlight{background:var(--doc-highlight-fill);border-radius:2px;}
            .doc-root .t.doc-highlight{border:1px solid var(--doc-highlight-border);}
            .doc-root .doc-highlight-sidebar-preview{background:oklch(0.72 0.14 250 / 0.42);border-radius:2px;}
            .doc-root .t.doc-highlight-sidebar-preview{border:1px solid oklch(0.5 0.14 250);}
            .doc-root .page-container{
              margin: 0;
              box-sizing: border-box;
              max-width: 100%;
              overflow-x: hidden;
              box-shadow: none;
              border-bottom: 2px solid var(--color-muted);
            }
            .doc-root .page-container:last-child{border-bottom: none;}

            ${DOCUMENT_HIGHLIGHT_STYLES}

            /* Paragraph highlight for navigation */
            .doc-root .paragraph-highlight {
              background: oklch(from var(--primary) l c h / 0.15);
              border-radius: 4px;
              transition: background-color 0.3s ease;
            }

            /* Legislation version comparison diff styling (M-23) */
            /* ins.diffins = added in new version — soft green underline, muted fill */
            .doc-root ins.diffins,
            .doc-root ins.diffmod {
              text-decoration: underline;
              text-decoration-color: hsl(142 60% 42% / 0.7);
              text-decoration-thickness: 1.5px;
              background-color: hsl(142 71% 45% / 0.10);
              color: inherit;
            }
            /* del.diffdel = removed from new version — soft red strikethrough, muted fill */
            .doc-root del.diffdel,
            .doc-root del.diffmod {
              text-decoration: line-through;
              text-decoration-color: hsl(0 70% 50% / 0.65);
              background-color: hsl(0 84% 60% / 0.08);
              opacity: 0.85;
              color: inherit;
            }
            /* Reconstructed "before" / "after" views (ins/del stripped in JS) — highlight spans only */
            .doc-root span.diff-highlight-after {
              text-decoration: none;
              background-color: hsl(142 71% 45% / 0.14);
              border-radius: 2px;
              color: inherit;
            }
            .dark .doc-root span.diff-highlight-after {
              background-color: hsl(142 71% 45% / 0.20);
            }
            .doc-root span.diff-highlight-before {
              text-decoration: none;
              background-color: hsl(0 80% 60% / 0.12);
              border-radius: 2px;
              opacity: 1;
              color: inherit;
            }
            .dark .doc-root span.diff-highlight-before {
              background-color: hsl(0 80% 60% / 0.18);
            }
            /* Current change highlight for navigation — may be on block parents or inline elements.
               Uses background-color + a large left-extending box-shadow so that hanging list
               numbers (rendered via negative text-indent or negative margin into the margin area)
               are also covered. box-shadow paints behind the element's own text content, so it
               tints under "34)" style hanging numbers without affecting layout. */
            .doc-root .diff-current {
              background-color: hsl(35 95% 55% / 0.10);
              box-shadow: -80px 0 0 0 hsl(35 95% 55% / 0.10);
              border-radius: 2px;
            }
            /* Expand button — injected after the navigated diff paragraph in "Ændringer" view */
            .diff-expand-btn {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              margin-top: 2px;
              padding: 2px 8px;
              font-size: 0.7rem;
              line-height: 1.4;
              color: hsl(35 90% 40%);
              background: hsl(35 95% 55% / 0.08);
              border: 1px solid hsl(35 95% 55% / 0.35);
              border-radius: 4px;
              cursor: pointer;
              transition: background 0.15s, color 0.15s;
            }
            .diff-expand-btn:hover {
              background: hsl(35 95% 55% / 0.16);
            }
            .diff-expand-btn--open svg {
              transform: rotate(180deg);
            }
            .dark .diff-expand-btn {
              color: hsl(35 85% 70%);
              background: hsl(35 90% 55% / 0.10);
              border-color: hsl(35 90% 55% / 0.30);
            }
            .dark .diff-expand-btn:hover {
              background: hsl(35 90% 55% / 0.18);
            }

            /* Fold-out panel — shows clean before/after versions of the navigated paragraph */
            .diff-foldout {
              margin: 6px 0 12px 0;
              border-radius: 6px;
              border: 1px solid hsl(35 95% 55% / 0.25);
              font-size: inherit;
            }
            .dark .diff-foldout {
              border-color: hsl(35 90% 55% / 0.20);
            }

            /* Before panel — amber left accent matching compare-target timeline dot.
               padding-left is generous to accommodate hanging list numbers (e.g. "4)")
               that the document CSS pushes into the margin via negative text-indent. */
            .diff-foldout-before {
              padding: 8px 12px 8px 40px;
              border-left: 3px solid hsl(35 95% 55%);
              background: hsl(35 95% 55% / 0.05);
              overflow: visible;
            }
            .dark .diff-foldout-before {
              background: hsl(35 90% 55% / 0.07);
              border-left-color: hsl(35 90% 65%);
            }

            /* After panel — primary left accent matching current-doc timeline dot */
            .diff-foldout-after {
              padding: 8px 12px 8px 40px;
              border-left: 3px solid hsl(var(--primary));
              border-top: 1px solid hsl(35 95% 55% / 0.15);
              background: hsl(var(--primary) / 0.04);
              overflow: visible;
            }
            .dark .diff-foldout-after {
              background: hsl(var(--primary) / 0.07);
              border-top-color: hsl(35 90% 55% / 0.12);
            }

            /* Version name label above each panel's content */
            .diff-foldout-label {
              display: block;
              font-size: 0.65rem;
              font-weight: 600;
              letter-spacing: 0.02em;
              text-transform: uppercase;
              color: hsl(35 70% 45%);
              margin-bottom: 4px;
            }
            .diff-foldout-after .diff-foldout-label {
              color: hsl(var(--primary));
            }
            .dark .diff-foldout-label {
              color: hsl(35 80% 65%);
            }
            .dark .diff-foldout-after .diff-foldout-label {
              color: hsl(var(--primary));
            }

            /* Content inherits all document styles — no diff markup inside */
            .diff-foldout-content {
              line-height: inherit;
            }

            /* Ensure document links are clickable */
            .doc-root .pdf-link[id^="doclink_"] {
              pointer-events: auto !important;
              cursor: pointer !important;
            }

            /* Dropdown menu trigger should inherit anchor styling */
            .doc-root button[data-slot="dropdown-menu-trigger"] {
              color: inherit !important;
            }
            .doc-root button[data-slot="dropdown-menu-trigger"] a {
              color: inherit !important;
            }

            /* Remove default button styling */
            .doc-root button[data-slot="dropdown-menu-trigger"] {
              all: unset !important;
            }
            /* Dropdown anchor should remove weird left margin */
            .doc-root .doc-link-anchor {
              text-indent: 0 !important;
              text-decoration: none;
              /* Leading/trailing spaces inside inline <a> collapse with white-space: normal;
                 match surrounding paragraph text (split reference segments). */
              white-space: pre-wrap;
            }
            .doc-root .doc-link-plain {
              text-decoration: none;
              color: inherit;
              white-space: pre-wrap;
            }
            /* Split merged reference: underline every segment while pointer is on any segment (doc-link-group-active from store) */
            .doc-root .doc-link-anchor:hover,
            .doc-root .doc-link-anchor.doc-link-group-active {
              text-decoration: underline;
            }

            /* Domsdatabasen responsive overrides */
            .doc-root .ddb-conv-doc .ddb-table {
              width: 100% !important;
              max-width: 1024px !important;
              overflow-x: auto;
            }
            .doc-root .ddb-conv-doc .page {
              width: 100% !important;
              max-width: 1024px !important;
              margin: 0 auto !important;
            }
            .doc-root .ddb-conv-doc .ddb-block,
            .doc-root .ddb-conv-doc .ddb-block-nb,
            .doc-root .ddb-conv-doc .text {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }

            ${DOCUMENT_WATERMARK_STYLES}
          `,
        }}
      />
      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Baseline dark text to beat inline colors */
            .dark .doc-root { color: rgb(235,235,235) !important; }
            .dark .doc-root a { color: rgb(180, 220, 255) !important; }

            /* Override inline foreground colors from the source */
            .dark .doc-root [style*="color:"] { color: rgb(235,235,235) !important; }
            /* Keep headings slightly brighter if inline colored */
            .dark .doc-root h1[style*="color:"],
            .dark .doc-root h2[style*="color:"],
            .dark .doc-root h3[style*="color:"] { color: rgb(245,245,245) !important; }

            /* Neutralize very light inline backgrounds */
            .dark .doc-root [style*="background-color:"] { background-color: rgb(28,28,28) !important; }
            /* Border/outline colors that were light on white */
            .dark .doc-root [style*="border-color:"] { border-color: rgb(80,80,80) !important; }
            .dark .doc-root [style*="outline-color:"] { outline-color: rgb(80,80,80) !important; }

            /* Re-assert highlight look on dark after the generic overrides */
            .dark .doc-root .doc-highlight{background:var(--doc-highlight-fill);border-radius:2px;}
            .dark .doc-root .t.doc-highlight{border:1px solid var(--doc-highlight-border);}
            .dark .doc-root .doc-highlight-sidebar-preview{background:oklch(0.55 0.12 250 / 0.55);border-radius:2px;}
            .dark .doc-root .t.doc-highlight-sidebar-preview{border:1px solid oklch(0.7 0.08 250);}
            .dark .doc-root .page-container{
              margin: 0;
              box-sizing: border-box;
              max-width: 100%;
              overflow-x: hidden;
              box-shadow: none;
              border-bottom: 2px solid var(--color-muted);
            }
            .dark .doc-root .page-container:last-child{border-bottom: none;}

            /* Paragraph highlight for dark mode */
            .dark .doc-root .paragraph-highlight {
              background: oklch(from var(--primary) l c h / 0.25);
            }

            /* Diff styling for dark mode */
            .dark .doc-root ins.diffins,
            .dark .doc-root ins.diffmod {
              background-color: hsl(142 60% 35% / 0.25) !important;
              text-decoration-color: hsl(142 71% 55%) !important;
            }
            .dark .doc-root del.diffdel,
            .dark .doc-root del.diffmod {
              background-color: hsl(0 70% 40% / 0.20) !important;
              text-decoration-color: hsl(0 80% 65%) !important;
            }
            /* Current change highlight for dark mode */
            .dark .doc-root .diff-current {
              background-color: hsl(35 90% 55% / 0.15) !important;
              box-shadow: -80px 0 0 0 hsl(35 90% 55% / 0.15) !important;
            }
          `,
        }}
      />
      {shouldGenerateDarkModeCss && darkModeCss && (
        <style dangerouslySetInnerHTML={{ __html: darkModeCss }} />
      )}
    </>
  );
};
